// /account/privacy
//
// Moved here from /account (AccountSettings) so the Account page can stay
// focused on day-to-day actions (profile, email, subscription management).
// Patients reach this page from a discrete link at the bottom of the
// Account page and via the footer "Privacy & data rights" link, which
// keeps the regulatory surface accessible without making it the first
// thing every patient sees on every visit.

import { useEffect, useState } from "react";
import { useSession } from "@/hooks/useSession";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { setCookieConsent } from "@/lib/cookieConsent";
import { AppLayout } from "@/components/Layout";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { Alert } from "@/components/Alert";
import { PageHeader } from "@/components/PageHeader";

export default function PrivacyAndData() {
  const { session } = useSession();
  const email = session?.user.email ?? "";

  return (
    <AppLayout>
      <PageHeader
        title="Privacy & data"
        subtitle="Your rights under HIPAA, CCPA/CPRA, and how we use cookies."
      />
      <div className="grid grid-cols-1 gap-4">
        <PrivacyRightsCard currentEmail={email} />
        <CookiePreferencesCard />
      </div>
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Privacy & Data Rights ------------------------------------------------------
// Surfaces every patient-facing right we promise in the Privacy Policy, HIPAA
// NPP, and California Residents page in one place so a regulator (or patient)
// can see they're real, not just words on the policy. Each action is wired
// either as a direct in-portal flow (where we have one) or as a structured
// mailto: that lands at our privacy mailbox with a pre-filled subject line.
function PrivacyRightsCard({ currentEmail }: { currentEmail: string }) {
  // mailto: helper so the request lands with a structured subject line our
  // privacy mailbox can route. Subject line matches the verbatim wording in
  // the CCPA / HIPAA / Privacy Policy pages so it's auditable end-to-end.
  function privacyMailto(action: string, body: string) {
    const subject = `Privacy Request — ${action}`;
    const params = new URLSearchParams({
      subject,
      body: `${body}\n\nRequested from account: ${currentEmail}\nDate: ${new Date().toISOString()}\n`,
    });
    return `mailto:privacy@fortehealth.co?${params.toString()}`;
  }

  const rights: Array<{
    title: string;
    description: string;
    cta: string;
    href: string;
    note?: string;
  }> = [
    {
      title: "Access your medical record",
      description:
        "Inspect or download a copy of your health information held by ForteHealth and our affiliated clinicians.",
      cta: "Request access",
      href: privacyMailto(
        "Access my medical record",
        "I am requesting a copy of my medical record under HIPAA 45 CFR 164.524.",
      ),
      note: "Fulfilled within 30 days under HIPAA.",
    },
    {
      title: "Correct your information",
      description:
        "Ask us to correct inaccurate or incomplete information in your medical record or account.",
      cta: "Request correction",
      href: privacyMailto(
        "Correct my information",
        "I am requesting a correction to my record. Please describe what should be changed: [your edits here]",
      ),
    },
    {
      title: "Restrict how we use your information",
      description:
        "Ask us to limit how we use or share your medical information for treatment, payment, or operations.",
      cta: "Request restriction",
      href: privacyMailto(
        "Restrict use or disclosure",
        "I am requesting a restriction on how my information is used. Please describe the restriction: [your request here]",
      ),
    },
    {
      title: "Accounting of disclosures",
      description:
        "Request a list of certain disclosures of your information we've made (per HIPAA 45 CFR 164.528).",
      cta: "Request list",
      href: privacyMailto(
        "Accounting of disclosures",
        "I am requesting an accounting of disclosures of my information under HIPAA 45 CFR 164.528 for the past 6 years (or shorter period).",
      ),
    },
    {
      title: "Delete your information",
      description:
        "Request deletion of personal information we hold. Note: medical records cannot be deleted within the legally required retention period.",
      cta: "Request deletion",
      href: privacyMailto(
        "Delete my information",
        "I am requesting deletion of my personal information to the extent permitted by law.",
      ),
    },
    {
      title: "Do Not Sell or Share My Personal Information",
      description:
        "Opt out of any sharing of your personal information for cross-context behavioral advertising (CCPA / CPRA).",
      cta: "Opt out",
      href: privacyMailto(
        "Do Not Sell or Share — CCPA opt-out",
        "I am exercising my right under CCPA/CPRA to opt out of the sale and sharing of my personal information.",
      ),
      note: "California residents. We also honor Global Privacy Control browser signals.",
    },
    {
      title: "Limit Use of Sensitive Personal Information",
      description:
        "Restrict our use of sensitive personal information (including health information) to providing the services you've asked for.",
      cta: "Limit use",
      href: privacyMailto(
        "Limit Use of Sensitive PI — CPRA",
        "I am exercising my right under CPRA to limit the use of my sensitive personal information.",
      ),
      note: "California residents.",
    },
    {
      title: "Download a portable copy",
      description:
        "Receive your personal information in a portable, machine-readable format.",
      cta: "Request data export",
      href: privacyMailto(
        "Data portability request",
        "I am requesting a portable, machine-readable copy of my personal information.",
      ),
    },
    {
      title: "File a privacy complaint",
      description:
        "If you believe your privacy rights have been violated, file a complaint with us or with HHS Office for Civil Rights.",
      cta: "File complaint",
      href: privacyMailto(
        "Privacy complaint",
        "I would like to file a privacy complaint regarding the following: [describe your concern here]",
      ),
      note: "No retaliation for filing a complaint.",
    },
  ];

  // Post-consolidation inventory: California rights fold into the Privacy
  // Policy; refunds/cancellation fold into Terms; patient rights live in
  // the HIPAA NPP.
  const legalLinks: Array<{ label: string; href: string }> = [
    { label: "Privacy Policy (incl. California rights)", href: "https://fortehealth.co/privacy" },
    { label: "Notice of Privacy Practices (HIPAA)", href: "https://fortehealth.co/hipaa" },
    { label: "Consumer Health Data Privacy", href: "https://fortehealth.co/consumer-health-data" },
    { label: "Telehealth Informed Consent", href: "https://fortehealth.co/telehealth-consent" },
    { label: "Terms of Service (incl. refunds & cancellation)", href: "https://fortehealth.co/terms" },
  ];

  return (
    <Card
      title="Privacy & data rights"
      action={
        <span className="text-[11px] font-semibold uppercase tracking-wider text-fh-gray">
          HIPAA · CCPA · CPRA
        </span>
      }
    >
      <p className="text-[14px] text-fh-gray leading-relaxed mb-4">
        Exercise any of your rights under HIPAA, the CCPA/CPRA, and our{" "}
        <a
          href="https://fortehealth.co/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-fh-green hover:text-fh-green-dark font-semibold underline"
        >
          Privacy Policy
        </a>
        . Most requests are fulfilled within 30 days; some California-specific
        requests within 45.
      </p>

      <ul className="border border-fh-border rounded-[10px] divide-y divide-fh-border">
        {rights.map((r) => (
          <li key={r.title} className="p-3.5 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] font-semibold text-fh-ink">{r.title}</div>
              <div className="text-[12.5px] text-fh-gray leading-snug mt-0.5">
                {r.description}
              </div>
              {r.note && (
                <div className="text-[11.5px] text-fh-gray-light leading-snug mt-1.5 italic">
                  {r.note}
                </div>
              )}
            </div>
            <a
              href={r.href}
              className="shrink-0 inline-flex items-center justify-center px-3 py-1.5 rounded-[8px] border border-fh-border bg-white text-[12.5px] font-semibold text-fh-ink hover:text-fh-green-dark hover:border-fh-green transition"
            >
              {r.cta}
            </a>
          </li>
        ))}
      </ul>

      <div className="mt-5 pt-4 border-t border-fh-border">
        <div className="text-[12px] font-semibold uppercase tracking-wider text-fh-gray mb-2">
          Read the policies
        </div>
        <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-[13px]">
          {legalLinks.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-fh-green hover:text-fh-green-dark underline"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Cookie preferences --------------------------------------------------------
// Lets returning patients revise the consent they recorded via the bottom
// banner. Strictly-necessary cookies are always on; analytics + marketing
// are toggles that immediately persist to localStorage.
function CookiePreferencesCard() {
  const consent = useCookieConsent();
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);
  const [marketing, setMarketing] = useState(consent?.marketing ?? false);
  const [saved, setSaved] = useState(false);

  // Keep local UI state in sync if another tab updates the consent (the hook
  // listens to onCookieConsentChange under the hood).
  useEffect(() => {
    if (!consent) return;
    setAnalytics(consent.analytics);
    setMarketing(consent.marketing);
  }, [consent]);

  function save() {
    setCookieConsent({ analytics, marketing });
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2400);
  }

  const dirty =
    consent == null ||
    consent.analytics !== analytics ||
    consent.marketing !== marketing;

  return (
    <Card title="Cookie preferences" action="Privacy controls">
      {saved && (
        <div className="mb-4">
          <Alert tone="success">Cookie preferences saved.</Alert>
        </div>
      )}
      <p className="text-[14px] text-fh-gray leading-relaxed mb-4">
        Manage how ForteHealth uses cookies on the patient portal and intake.
        Strictly-necessary cookies are always on so sign-in and security work;
        you can opt in or out of the others anytime.
      </p>
      <div className="border border-fh-border rounded-[10px] divide-y divide-fh-border">
        <CookieRow
          title="Strictly necessary"
          description="Required for sign-in, security, and basic site function. Always on."
          checked
          disabled
        />
        <CookieRow
          title="Analytics"
          description="Helps us understand which pages and features patients find useful. No PHI is shared."
          checked={analytics}
          onChange={setAnalytics}
        />
        <CookieRow
          title="Marketing"
          description="Lets us measure ad performance so we can reach people seeking GLP-1 or TRT care."
          checked={marketing}
          onChange={setMarketing}
        />
      </div>
      <div className="mt-4 flex items-center gap-2">
        <Button onClick={save} disabled={!dirty}>
          Save preferences
        </Button>
        {consent?.decidedAt && (
          <span className="text-[12px] text-fh-gray-light">
            Last updated {new Date(consent.decidedAt).toLocaleDateString()}
          </span>
        )}
      </div>
    </Card>
  );
}

function CookieRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 p-3.5 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-1 w-4 h-4 accent-fh-green-dark disabled:opacity-60 disabled:cursor-not-allowed"
      />
      <div className="flex-1">
        <div className="text-[13.5px] font-semibold text-fh-ink">{title}</div>
        <div className="text-[12.5px] text-fh-gray leading-snug">{description}</div>
      </div>
    </label>
  );
}
